import { useCallback, useEffect, useState } from "react";
import { useMounted } from "src/hooks/use-mounted";
import { useTranslation } from "react-i18next";
import { tokens } from "src/locales/tokens";
import { format } from "date-fns";
import {
  Box,
  Container,
  Unstable_Grid2 as Grid,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { Seo } from "src/components/seo";
import { Logo } from "src/components/logo";
import { OverviewPublicWQIStatus } from "src/sections/dashboard/overview-public-wqi-status";
import { OverviewPublicTable } from "src/sections/dashboard/overview-public-table";
import { OverviewPublicMap } from "src/sections/dashboard/overview-public-map";
import { MaterialUISwitch } from "src/components/ui-switch";
import { ThemeModeProvider, useThemeMode } from "src/components/theme-mode";
import { publicApi } from "src/api/public";

const useDashboardSearch = () => {
  const compareTimeWQ =
    window.localStorage.getItem("CT_WQ_PUB") || "15 MINUTES";
  const [state, setState] = useState({
    compareTimeWQ: compareTimeWQ,
    autoLoad: true,
    loading: true,
  });

  const handleCompareTimeWQChange = useCallback((compareTimeWQ) => {
    window.localStorage.setItem("CT_WQ_PUB", compareTimeWQ);
    setState((prevState) => ({
      ...prevState,
      compareTimeWQ,
      loading: true,
    }));
  }, []);

  return {
    handleCompareTimeWQChange,
    state,
  };
};

const useDashboardStore = (searchState) => {
  const isMounted = useMounted();
  const [state, setState] = useState({
    overview: {},
    geolocations: [],
    stations: [],
    lastDataRefreshed: null,
  });

  const handleDashboardGet = useCallback(async () => {
    try {
      searchState.loading = true;

      await publicApi.prepare();

      const response = await publicApi.getOverview(searchState);

      searchState.loading = false;

      if (isMounted()) {
        setState({
          overview: response.overview,
          stations: response.stations,
          lastDataRefreshed: format(new Date(), "dd/MM/yyyy HH:mm:ss"),
        });
      }
    } catch (err) {
      console.error(err);
    }
  }, [searchState, isMounted]);

  useEffect(
    () => {
      let timeoutId;

      const executeAtIntervals = () => {
        const now = new Date();
        const currentMinutes = now.getMinutes();

        const nextTargetMinute =
          [15, 30, 45, 0].find((minute) => minute > currentMinutes) || 0;
        const isNextHour = nextTargetMinute === 0 && currentMinutes >= 45;
        const nextHour = isNextHour ? now.getHours() + 1 : now.getHours();

        const nextTargetTime = new Date(now);
        nextTargetTime.setHours(nextHour, nextTargetMinute, 15, 0);

        const timeUntilNextExecution = nextTargetTime - now; // Time in milliseconds until next execution

        timeoutId = setTimeout(() => {
          if (searchState.autoLoad) {
            handleDashboardGet();
            executeAtIntervals();
          }
        }, timeUntilNextExecution);
      };

      if (searchState.autoLoad) {
        handleDashboardGet();
        executeAtIntervals();
      }

      return () => clearTimeout(timeoutId);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchState]
  );

  return {
    ...state,
  };
};

const Page = () => {
  const { mode, toggleMode } = useThemeMode();
  const { t } = useTranslation();

  const dashboardSearch = useDashboardSearch();
  const dashboardStore = useDashboardStore(dashboardSearch.state);
  const [isLoading, setIsLoading] = useState(dashboardSearch.state.loading);
  const [zoomToStation, setZoomToStation] = useState(null);

  const overview = dashboardStore.overview;
  const wqiStatusCount = overview.wqiStatusCount;
  const lastDataRefreshed = dashboardStore.lastDataRefreshed;

  useEffect(() => {
    setIsLoading(dashboardSearch.state.loading);
    document.documentElement.classList.toggle("dark-mode", mode === "dark");
  }, [dashboardSearch.state.loading, mode]);

  const handleZoomStationChange = useCallback((station = {}) => {
    setZoomToStation(station);
  }, []);

  const handleModeSwitch = () => {
    toggleMode();
  };

  return (
    <>
      <Seo title={"Indeks Kualiti Air"} />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          pt: 1,
          backgroundColor: (theme) =>
            theme.palette.mode === "dark" ? "#0E1320" : "#fff",
        }}
      >
        <Container maxWidth={false}>
          <Grid
            container
            disableEqualOverflow
            spacing={{
              xs: 2,
              lg: 2,
            }}
          >
            <Grid xs={12}>
              <Stack
                direction={"row"}
                justifyContent="space-between"
                alignItems={"center"}
                spacing={4}
                sx={{
                  pt: 2,
                }}
              >
                <Stack
                  alignItems="center"
                  direction="row"
                  spacing={2}
                >
                  <Logo width={48} />
                  <Stack
                    direction={"column"}
                    sx={{
                      color: (theme) =>
                        theme.palette.mode === "dark" ? "#EDF2F7" : "#111927",
                    }}
                  >
                    <Typography variant="h5">
                      {t(tokens.label.clientName)}
                    </Typography>
                    <Typography variant="h6">Indeks Kualiti Air</Typography>
                  </Stack>
                </Stack>
                <Stack
                  alignItems="center"
                  direction="row"
                  flexWrap="wrap"
                  gap={1}
                >
                  <Tooltip
                    title={mode === "light" ? "Light Mode" : "Dark Mode"}
                  >
                    <MaterialUISwitch
                      sx={{ m: 1 }}
                      checked={mode === "dark"}
                      onClick={handleModeSwitch}
                    />
                  </Tooltip>
                </Stack>
              </Stack>
            </Grid>
            <Grid
              md={3}
              sm={12}
              xs={12}
            >
              <Stack
                direction="column"
                spacing={{
                  xs: 2,
                  lg: 2,
                }}
              >
                <OverviewPublicWQIStatus
                  status={wqiStatusCount}
                  isLoading={isLoading}
                />
                <OverviewPublicTable
                  tableData={overview?.wqiReadings}
                  lastDataRefreshed={lastDataRefreshed}
                  currentCompareTime={dashboardSearch.state.compareTimeWQ}
                  onCompareTimeChange={
                    dashboardSearch.handleCompareTimeWQChange
                  }
                  onZoomToMap={handleZoomStationChange}
                  isLoading={isLoading}
                />
              </Stack>
            </Grid>
            <Grid
              md={9}
              sm={12}
              xs={12}
            >
              <Stack
                sx={{
                  borderRadius: 2,
                  pb: 2,
                }}
              >
                <OverviewPublicMap
                  height={"89vh"}
                  geolocations={dashboardStore.overview.geolocations}
                  selectedStation={zoomToStation}
                  isLoading={isLoading}
                />
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </>
  );
};

const ThemedPage = () => (
  <ThemeModeProvider>
    <Page />
  </ThemeModeProvider>
);

export default ThemedPage;
